/**
 * FERP Portal - 25 Primitive Repositories
 *
 * Aligned to the running gateway schema (introspected Feb 2026).
 * Organized by FERP lifecycle phase:
 * - DESIGN (5): Workflow, Template, Dependency, Policy, Role
 * - CONFIGURE (5): Team, Actor, Validation, Contract, Constraint
 * - EXECUTE (5): Instance, Task, Approval, Document, Queue
 * - MONITOR (5): Event, Notification, Trace, Checkpoint, Transaction
 * - OPTIMIZE (5): Audit, Analytics, Schedule, Resource, Integration
 *
 * Each repository maps to the exact query names, arguments, return types,
 * and field selections exposed by the ferp-gateway GraphQL schema.
 */

// eslint-disable-next-line no-restricted-imports -- Repository definitions require gql tag
import { gql } from '@apollo/client';
import { createRepository, type BaseEntity } from '../createRepository';
import type { BackendId } from '@/types/branded-ids';

// ============================================================================
// DESIGN PHASE PRIMITIVES (5)
// ============================================================================

// --- Workflow ---
// AuthCtx tenant (no tenantId arg). Returns WorkflowConnection { items, totalCount, hasNextPage }
export interface Workflow extends BaseEntity {
  id: BackendId;
  tenantId: string;
  name: string;
  description?: string;
  version: number;
  executionStrategy?: string;
  active: boolean;
  criticality?: string;
  compressedId?: string;
  metadata?: Record<string, unknown>;
}

export const useWorkflowRepository = createRepository<Workflow>({
  entityName: 'Workflow',
  listQuery: gql`
    query ListWorkflows($limit: Int, $cursor: String) {
      listWorkflows(limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          name
          description
          version
          executionStrategy
          active
          criticality
          compressedId
        }
        totalCount
        hasNextPage
      }
    }
  `,
  getQuery: gql`
    query GetWorkflow($workflowId: String!) {
      getWorkflow(workflowId: $workflowId) {
        id
        tenantId
        name
        description
        version
        executionStrategy
        active
        criticality
        metadata
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listWorkflows?.items || [],
  extractEntity: (data: any) => data?.getWorkflow,
  idFieldName: 'workflowId',
});

// --- Template ---
// AuthCtx tenant. Returns GqlTemplateConnection { items, total, nextCursor, hasMore }
export interface Template extends BaseEntity {
  id: BackendId;
  tenantId: string;
  name: string;
  description?: string;
  version: number;
  status: string;
  category?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useTemplateRepository = createRepository<Template>({
  entityName: 'Template',
  listQuery: gql`
    query ListTemplates($limit: Int!, $cursor: String) {
      listTemplates(limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          name
          description
          version
          status
          category
          active
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetTemplate($templateId: String!) {
      getTemplate(templateId: $templateId) {
        id
        tenantId
        name
        description
        version
        status
        category
        active
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listTemplates?.items || [],
  extractEntity: (data: any) => data?.getTemplate,
  idFieldName: 'templateId',
});

// --- Dependency ---
// List requires workflowId. Get requires tenantId + dependencyId.
// Returns DependencyConnection { items, nextCursor, hasMore }
export interface Dependency extends BaseEntity {
  id: BackendId;
  tenantId: string;
  workflowId: string;
  name: string;
  description?: string;
  predecessorId: string;
  successorId: string;
  dependencyType: string;
  isConditional: boolean;
  status: string;
  active: boolean;
  version: number;
  compressedId?: string;
}

export const useDependencyRepository = createRepository<Dependency>({
  entityName: 'Dependency',
  listQuery: gql`
    query ListDependencies($workflowId: String!, $limit: Int, $cursor: String) {
      listDependenciesByWorkflow(workflowId: $workflowId, limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          workflowId
          name
          description
          predecessorId
          successorId
          dependencyType
          isConditional
          status
          active
          version
          compressedId
        }
        nextCursor
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetDependency($tenantId: String!, $dependencyId: String!) {
      getDependency(tenantId: $tenantId, dependencyId: $dependencyId) {
        id
        tenantId
        workflowId
        name
        description
        predecessorId
        successorId
        dependencyType
        isConditional
        status
        active
        version
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listDependenciesByWorkflow?.items || [],
  extractEntity: (data: any) => data?.getDependency,
  idFieldName: 'dependencyId',
});

// --- Policy ---
// Explicit tenantId arg. Returns PolicyConnection { items, nextCursor, hasMore }
export interface Policy extends BaseEntity {
  id: BackendId;
  tenantId: string;
  name: string;
  description?: string;
  policyType: string;
  scope: string;
  enabled: boolean;
  active: boolean;
  version: number;
  compressedId?: string;
}

export const usePolicyRepository = createRepository<Policy>({
  entityName: 'Policy',
  listQuery: gql`
    query ListPolicies($tenantId: String!, $limit: Int, $cursor: String) {
      listPolicies(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          name
          description
          policyType
          scope
          enabled
          active
          version
          compressedId
        }
        nextCursor
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetPolicy($tenantId: String!, $policyId: String!) {
      getPolicy(tenantId: $tenantId, policyId: $policyId) {
        id
        tenantId
        name
        description
        policyType
        scope
        enabled
        active
        version
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listPolicies?.items || [],
  extractEntity: (data: any) => data?.getPolicy,
  idFieldName: 'policyId',
});

// --- Role ---
// AuthCtx tenant. Returns RoleConnection { items, nextCursor, hasMore }
export interface Role extends BaseEntity {
  id: BackendId;
  tenantId: string;
  roleName: string;
  description?: string;
  roleCategory: string;
  active: boolean;
  version: number;
  compressedId?: string;
}

export const useRoleRepository = createRepository<Role>({
  entityName: 'Role',
  listQuery: gql`
    query ListRoles($limit: Int, $cursor: String) {
      listRoles(limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          roleName
          description
          roleCategory
          active
          version
          compressedId
        }
        nextCursor
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetRole($roleId: String!) {
      getRole(roleId: $roleId) {
        id
        tenantId
        roleName
        description
        roleCategory
        permissions
        active
        version
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listRoles?.items || [],
  extractEntity: (data: any) => data?.getRole,
  idFieldName: 'roleId',
});

// ============================================================================
// CONFIGURE PHASE PRIMITIVES (5)
// ============================================================================

// --- Team ---
// AuthCtx tenant. Returns flat [Team]! (no connection wrapper)
export interface Team extends BaseEntity {
  id: BackendId;
  tenantId: string;
  name: string;
  code: string;
  description?: string;
  teamType: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useTeamRepository = createRepository<Team>({
  entityName: 'Team',
  listQuery: gql`
    query ListTeams($limit: Int) {
      listTeams(limit: $limit) {
        id
        tenantId
        name
        code
        description
        teamType
        active
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  getQuery: gql`
    query GetTeam($code: String!) {
      getTeam(code: $code) {
        id
        tenantId
        name
        code
        description
        teamType
        active
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listTeams || [],
  extractEntity: (data: any) => data?.getTeam,
  idFieldName: 'code',
});

// --- Actor ---
// AuthCtx tenant. Returns GqlActorConnection { items, total, nextCursor, hasMore }
export interface Actor extends BaseEntity {
  id: BackendId;
  name: string;
  version: number;
  actorType: string;
  category?: string;
  description?: string;
  status: string;
  available: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useActorRepository = createRepository<Actor>({
  entityName: 'Actor',
  listQuery: gql`
    query ListActors($limit: Int, $cursor: String) {
      listActors(limit: $limit, cursor: $cursor) {
        items {
          id
          name
          version
          actorType
          category
          description
          status
          available
          active
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetActor($actorId: String!) {
      getActor(actorId: $actorId) {
        id
        name
        version
        actorType
        category
        description
        status
        available
        active
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listActors?.items || [],
  extractEntity: (data: any) => data?.getActor,
  idFieldName: 'actorId',
});

// --- Validation ---
// AuthCtx tenant. Returns flat [Validation]! (no connection wrapper)
export interface Validation extends BaseEntity {
  id: BackendId;
  tenantId: string;
  name: string;
  code: string;
  description?: string;
  validationType: string;
  severity: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useValidationRepository = createRepository<Validation>({
  entityName: 'Validation',
  listQuery: gql`
    query ListValidations($limit: Int) {
      listValidations(limit: $limit) {
        id
        tenantId
        name
        code
        description
        validationType
        severity
        active
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  getQuery: gql`
    query GetValidation($code: String!) {
      getValidation(code: $code) {
        id
        tenantId
        name
        code
        description
        validationType
        severity
        active
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listValidations || [],
  extractEntity: (data: any) => data?.getValidation,
  idFieldName: 'code',
});

// --- Contract ---
// AuthCtx tenant. List requires workflowInstanceId. Returns GqlContractConnection { items, total, nextCursor, hasMore }
export interface Contract extends BaseEntity {
  id: BackendId;
  tenantId: string;
  slaName: string;
  slug?: string;
  externalId?: string;
  currentStatus: string;
  responseTimeMinutes?: number;
  resolutionTimeMinutes?: number;
  breachCount?: number;
  complianceRate?: number;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useContractRepository = createRepository<Contract>({
  entityName: 'Contract',
  listQuery: gql`
    query ListContracts($workflowInstanceId: String!, $limit: Int!) {
      listContractsByWorkflow(workflowInstanceId: $workflowInstanceId, limit: $limit) {
        items {
          id
          tenantId
          slaName
          slug
          externalId
          currentStatus
          responseTimeMinutes
          resolutionTimeMinutes
          breachCount
          complianceRate
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetContract($workflowInstanceId: String!, $contractId: String!) {
      getContract(workflowInstanceId: $workflowInstanceId, contractId: $contractId) {
        id
        tenantId
        slaName
        slug
        externalId
        currentStatus
        responseTimeMinutes
        resolutionTimeMinutes
        breachCount
        complianceRate
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listContractsByWorkflow?.items || [],
  extractEntity: (data: any) => data?.getContract,
  idFieldName: 'contractId',
});

// --- Constraint ---
// AuthCtx tenant. Returns GqlConstraintConnection { items, total, nextCursor, hasMore }
export interface Constraint extends BaseEntity {
  id: BackendId;
  tenantId: string;
  name: string;
  code: string;
  description?: string;
  constraintType: string;
  currentStatus: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useConstraintRepository = createRepository<Constraint>({
  entityName: 'Constraint',
  listQuery: gql`
    query ListConstraints($limit: Int, $cursor: String) {
      listConstraints(limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          name
          code
          description
          constraintType
          currentStatus
          active
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetConstraint($code: String!) {
      getConstraint(code: $code) {
        id
        tenantId
        name
        code
        description
        constraintType
        currentStatus
        active
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listConstraints?.items || [],
  extractEntity: (data: any) => data?.getConstraint,
  idFieldName: 'code',
});

// ============================================================================
// EXECUTE PHASE PRIMITIVES (5)
// ============================================================================

// --- Instance ---
// Explicit tenantId arg. Returns GqlInstanceConnection { items, total, nextCursor, hasMore }
export interface Instance extends BaseEntity {
  id: BackendId;
  tenantId: string;
  instanceName: string;
  workflowDefinitionId: string;
  status: string;
  currentState?: string;
  progressPercent?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useInstanceRepository = createRepository<Instance>({
  entityName: 'Instance',
  listQuery: gql`
    query ListInstances($tenantId: String!, $limit: Int, $offset: Int) {
      listInstances(tenantId: $tenantId, limit: $limit, offset: $offset) {
        items {
          id
          tenantId
          instanceName
          workflowDefinitionId
          status
          currentState
          progressPercent
          startedAt
          completedAt
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetInstance($tenantId: String!, $instanceId: String!) {
      getInstance(tenantId: $tenantId, instanceId: $instanceId) {
        id
        tenantId
        instanceName
        workflowDefinitionId
        status
        currentState
        progressPercent
        startedAt
        completedAt
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listInstances?.items || [],
  extractEntity: (data: any) => data?.getInstance,
  idFieldName: 'instanceId',
});

// --- Task ---
// AuthCtx tenant. Returns GqlTaskConnection { items, total, nextCursor, hasMore }
export interface Task extends BaseEntity {
  id: BackendId;
  tenantId: string;
  workflowInstanceId: string;
  name: string;
  description?: string;
  taskType?: string;
  assignedActorId?: string;
  status: string;
  priority?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useTaskRepository = createRepository<Task>({
  entityName: 'Task',
  listQuery: gql`
    query ListTasks($limit: Int, $cursor: String) {
      taskApiListTasks(limit: $limit, cursor: $cursor) {
        tasks {
          id
          tenantId
          workflowInstanceId
          name
          description
          taskType
          assignedActorId
          status
          priority
          startedAt
          completedAt
          createdAt
          updatedAt
          compressedId
        }
        totalCount
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetTask($workflowInstanceId: String!, $taskId: String!) {
      taskApiGetTask(workflowInstanceId: $workflowInstanceId, taskId: $taskId) {
        id
        tenantId
        workflowInstanceId
        name
        description
        taskType
        assignedActorId
        status
        priority
        startedAt
        completedAt
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.taskApiListTasks?.tasks || [],
  extractEntity: (data: any) => data?.taskApiGetTask,
  idFieldName: 'taskId',
});

// --- Approval ---
// Explicit tenantId arg. List requires status. Returns GqlApprovalConnection { items, total, nextCursor, hasMore }
export interface Approval extends BaseEntity {
  id: BackendId;
  tenantId: string;
  approvalName: string;
  approvalType: string;
  status: string;
  priority?: string;
  approverId?: string;
  approverRole?: string;
  decision?: string;
  decisionReason?: string;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useApprovalRepository = createRepository<Approval>({
  entityName: 'Approval',
  listQuery: gql`
    query ListApprovals($tenantId: String!, $status: GqlApprovalStatus!, $limit: Int, $cursor: String) {
      listApprovalsByStatus(tenantId: $tenantId, status: $status, limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          approvalName
          approvalType
          status
          priority
          approverId
          approverRole
          decision
          decisionReason
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetApproval($tenantId: String!, $approvalId: String!) {
      getApproval(tenantId: $tenantId, approvalId: $approvalId) {
        id
        tenantId
        approvalName
        approvalType
        status
        priority
        approverId
        approverRole
        decision
        decisionReason
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listApprovalsByStatus?.items || [],
  extractEntity: (data: any) => data?.getApproval,
  idFieldName: 'approvalId',
});

// --- Document ---
// Explicit tenantId on list. Get by documentId only.
// Returns DocumentConnection { nodes, totalCount, hasNextPage, endCursor }
export interface Document extends BaseEntity {
  id: BackendId; // maps to documentId from gateway
  tenantId: string;
  documentName: string;
  documentType: string;
  category?: string;
  description?: string;
  currentStatus: string;
  version: number;
  isLatest: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useDocumentRepository = createRepository<Document>({
  entityName: 'Document',
  listQuery: gql`
    query ListDocuments($tenantId: String!, $limit: Int, $cursor: String) {
      listDocuments(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
        nodes {
          documentId
          tenantId
          documentName
          documentType
          category
          description
          currentStatus
          version
          isLatest
          createdAt
          updatedAt
          compressedId
        }
        totalCount
        hasNextPage
      }
    }
  `,
  getQuery: gql`
    query GetDocument($documentId: String!) {
      getDocument(documentId: $documentId) {
        documentId
        tenantId
        documentName
        documentType
        category
        description
        currentStatus
        version
        isLatest
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) =>
    (data?.listDocuments?.nodes || []).map((n: any) => ({ ...n, id: n.documentId })),
  extractEntity: (data: any) => {
    const d = data?.getDocument;
    return d ? { ...d, id: d.documentId } : null;
  },
  idFieldName: 'documentId',
});

// --- Queue ---
// Explicit tenantId arg. Returns GqlQueueConnection { items, total, nextCursor, hasMore }
export interface Queue extends BaseEntity {
  id: BackendId;
  tenantId: string;
  queueName: string;
  queueType?: string;
  description?: string;
  currentSize?: number;
  avgWaitTimeSeconds?: number;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useQueueRepository = createRepository<Queue>({
  entityName: 'Queue',
  listQuery: gql`
    query ListQueues($tenantId: String!, $limit: Int, $cursor: String) {
      listQueues(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          queueName
          queueType
          description
          currentSize
          avgWaitTimeSeconds
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetQueue($tenantId: String!, $queueId: String!) {
      getQueue(tenantId: $tenantId, queueId: $queueId) {
        id
        tenantId
        queueName
        queueType
        description
        currentSize
        avgWaitTimeSeconds
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listQueues?.items || [],
  extractEntity: (data: any) => data?.getQueue,
  idFieldName: 'queueId',
});

// ============================================================================
// MONITOR PHASE PRIMITIVES (5)
// ============================================================================

// --- Event ---
// Explicit tenantId arg. Returns GqlEventConnection { items, total, nextCursor, hasMore }
export interface Event extends BaseEntity {
  id: BackendId;
  tenantId: string;
  eventName: string;
  eventType: string;
  entityType?: string;
  entityId?: string;
  severity?: string;
  category?: string;
  source?: string;
  action?: string;
  createdAt: string;
  compressedId?: string;
}

export const useEventRepository = createRepository<Event>({
  entityName: 'Event',
  listQuery: gql`
    query ListEvents($tenantId: String!, $limit: Int, $cursor: String) {
      listEvents(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          eventName
          eventType
          entityType
          entityId
          severity
          category
          source
          action
          createdAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetEvent($tenantId: String!, $eventId: String!) {
      getEvent(tenantId: $tenantId, eventId: $eventId) {
        id
        tenantId
        eventName
        eventType
        entityType
        entityId
        severity
        category
        source
        action
        createdAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listEvents?.items || [],
  extractEntity: (data: any) => data?.getEvent,
  idFieldName: 'eventId',
});

// --- Notification ---
// AuthCtx tenant. List requires recipientActorId. Returns GqlNotificationConnection { items, total, nextCursor, hasMore }
export interface Notification extends BaseEntity {
  id: BackendId;
  tenantId: string;
  notificationTitle: string;
  notificationType: string;
  priority?: string;
  status: string;
  sentAt?: string;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useNotificationRepository = createRepository<Notification>({
  entityName: 'Notification',
  listQuery: gql`
    query ListNotifications($recipientActorId: String!, $limit: Int!) {
      listNotificationsForRecipient(recipientActorId: $recipientActorId, limit: $limit) {
        items {
          id
          tenantId
          notificationTitle
          notificationType
          priority
          status
          sentAt
          readAt
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetNotification($notificationId: String!) {
      getNotification(notificationId: $notificationId) {
        id
        tenantId
        notificationTitle
        notificationType
        priority
        status
        sentAt
        readAt
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listNotificationsForRecipient?.items || [],
  extractEntity: (data: any) => data?.getNotification,
  idFieldName: 'notificationId',
});

// --- Trace ---
// Explicit tenantId arg. List requires workflowInstanceId.
// Returns TraceConnection { items, nextCursor, hasMore }
export interface Trace extends BaseEntity {
  id: BackendId;
  tenantId: string;
  workflowInstanceId: string;
  traceId: string;
  spanId: string;
  operationName?: string;
  status: string;
  durationMs?: number;
  startedAt?: string;
  endedAt?: string;
  createdAt: string;
  compressedId?: string;
}

export const useTraceRepository = createRepository<Trace>({
  entityName: 'Trace',
  listQuery: gql`
    query ListTraces($tenantId: String!, $workflowInstanceId: String!, $limit: Int, $cursor: String) {
      listTracesByWorkflowInstance(tenantId: $tenantId, workflowInstanceId: $workflowInstanceId, limit: $limit, cursor: $cursor) {
        items {
          id
          tenantId
          workflowInstanceId
          traceId
          spanId
          operationName
          status
          durationMs
          startedAt
          endedAt
          createdAt
          compressedId
        }
        nextCursor
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetTrace($tenantId: String!, $traceId: String!, $spanId: String!) {
      getTrace(tenantId: $tenantId, traceId: $traceId, spanId: $spanId) {
        id
        tenantId
        workflowInstanceId
        traceId
        spanId
        operationName
        status
        durationMs
        startedAt
        endedAt
        createdAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listTracesByWorkflowInstance?.items || [],
  extractEntity: (data: any) => data?.getTrace,
  idFieldName: 'traceId',
});

// --- Checkpoint ---
// AuthCtx tenant. List requires workflowInstanceId. Returns flat [Checkpoint]!
export interface Checkpoint extends BaseEntity {
  id: BackendId;
  tenantId: string;
  checkpointName: string;
  entityType?: string;
  currentStatus: string;
  checkpointType?: string;
  canRollback: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useCheckpointRepository = createRepository<Checkpoint>({
  entityName: 'Checkpoint',
  listQuery: gql`
    query ListCheckpoints($workflowInstanceId: String!, $limit: Int) {
      listCheckpointsByWorkflow(workflowInstanceId: $workflowInstanceId, limit: $limit) {
        id
        tenantId
        checkpointName
        entityType
        currentStatus
        checkpointType
        canRollback
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  getQuery: gql`
    query GetCheckpoint($checkpointId: String!) {
      getCheckpoint(checkpointId: $checkpointId) {
        id
        tenantId
        checkpointName
        entityType
        currentStatus
        checkpointType
        canRollback
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) => data?.listCheckpointsByWorkflow || [],
  extractEntity: (data: any) => data?.getCheckpoint,
  idFieldName: 'checkpointId',
});

// --- Transaction ---
// AuthCtx tenant. Returns TransactionPage { transactions, nextToken }
export interface Transaction extends BaseEntity {
  id: BackendId; // maps to transactionId from gateway
  tenantId: string;
  transactionName: string;
  transactionCode?: string;
  status: string;
  isLifeCritical: boolean;
  startedAt?: string;
  committedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const useTransactionRepository = createRepository<Transaction>({
  entityName: 'Transaction',
  listQuery: gql`
    query ListTransactions($limit: Int, $lastEvaluatedKey: String) {
      listTransactionsByTenant(limit: $limit, lastEvaluatedKey: $lastEvaluatedKey) {
        transactions {
          transactionId
          tenantId
          transactionName
          transactionCode
          status
          isLifeCritical
          startedAt
          committedAt
          createdAt
          updatedAt
        }
        nextToken
      }
    }
  `,
  getQuery: gql`
    query GetTransaction($transactionId: String!) {
      getTransaction(transactionId: $transactionId) {
        transactionId
        tenantId
        transactionName
        transactionCode
        status
        isLifeCritical
        startedAt
        committedAt
        createdAt
        updatedAt
      }
    }
  `,
  extractList: (data: any) =>
    (data?.listTransactionsByTenant?.transactions || []).map((t: any) => ({ ...t, id: t.transactionId })),
  extractEntity: (data: any) => {
    const t = data?.getTransaction;
    return t ? { ...t, id: t.transactionId } : null;
  },
  idFieldName: 'transactionId',
});

// ============================================================================
// OPTIMIZE PHASE PRIMITIVES (5)
// ============================================================================

// --- Audit ---
// Explicit tenantId arg. Returns AuditListResultQL { audits, nextToken }
export interface AuditEntry extends BaseEntity {
  id: BackendId;
  tenantId: string;
  auditName: string;
  auditCode?: string;
  frameworkType?: string;
  complianceStatus?: string;
  violationsDetected?: number;
  createdAt: string;
}

export const useAuditRepository = createRepository<AuditEntry>({
  entityName: 'Audit',
  listQuery: gql`
    query ListAudits($tenantId: String!, $limit: Int, $nextToken: String) {
      listAudits(tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
        audits {
          id
          tenantId
          auditName
          auditCode
          frameworkType
          complianceStatus
          violationsDetected
          createdAt
        }
        nextToken
      }
    }
  `,
  getQuery: gql`
    query GetAudit($tenantId: String!, $id: ID!) {
      getAudit(tenantId: $tenantId, id: $id) {
        id
        tenantId
        auditName
        auditCode
        frameworkType
        complianceStatus
        violationsDetected
        createdAt
      }
    }
  `,
  extractList: (data: any) => data?.listAudits?.audits || [],
  extractEntity: (data: any) => data?.getAudit,
  idFieldName: 'id',
});

// --- Analytics ---
// Explicit tenantId arg. Returns AnalyticsConnection { nodes, totalCount, hasNextPage, endCursor }
export interface Analytics extends BaseEntity {
  id: BackendId; // maps to analyticsId from gateway
  tenantId: string;
  analyticsName: string;
  analyticsCode?: string;
  description?: string;
  metricType?: string;
  metricName?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useAnalyticsRepository = createRepository<Analytics>({
  entityName: 'Analytics',
  listQuery: gql`
    query ListAnalytics($tenantId: String!, $limit: Int, $cursor: String) {
      listAnalytics(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
        nodes {
          analyticsId
          tenantId
          analyticsName
          analyticsCode
          description
          metricType
          metricName
          enabled
          createdAt
          updatedAt
          compressedId
        }
        totalCount
        hasNextPage
      }
    }
  `,
  getQuery: gql`
    query GetAnalytics($tenantId: String!, $analyticsId: String!) {
      getAnalytics(tenantId: $tenantId, analyticsId: $analyticsId) {
        analyticsId
        tenantId
        analyticsName
        analyticsCode
        description
        metricType
        metricName
        enabled
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) =>
    (data?.listAnalytics?.nodes || []).map((n: any) => ({ ...n, id: n.analyticsId })),
  extractEntity: (data: any) => {
    const a = data?.getAnalytics;
    return a ? { ...a, id: a.analyticsId } : null;
  },
  idFieldName: 'analyticsId',
});

// --- Schedule ---
// AuthCtx tenant. Returns GqlScheduleConnection { items, total, nextCursor, hasMore }
export interface Schedule extends BaseEntity {
  id: BackendId; // maps to scheduleId from gateway
  tenantId: string;
  scheduleName: string;
  scheduleCode?: string;
  scheduleType?: string;
  scheduleStatus: string;
  schedulePriority?: string;
  isLifeCritical: boolean;
  cronExpression?: string;
  timezone?: string;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useScheduleRepository = createRepository<Schedule>({
  entityName: 'Schedule',
  listQuery: gql`
    query ListSchedules($limit: Int, $cursor: String) {
      listSchedules(limit: $limit, cursor: $cursor) {
        items {
          scheduleId
          tenantId
          scheduleName
          scheduleCode
          scheduleType
          scheduleStatus
          schedulePriority
          isLifeCritical
          cronExpression
          timezone
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetSchedule($scheduleId: String!) {
      getSchedule(scheduleId: $scheduleId) {
        scheduleId
        tenantId
        scheduleName
        scheduleCode
        scheduleType
        scheduleStatus
        schedulePriority
        isLifeCritical
        cronExpression
        timezone
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) =>
    (data?.listSchedules?.items || []).map((s: any) => ({ ...s, id: s.scheduleId })),
  extractEntity: (data: any) => {
    const s = data?.getSchedule;
    return s ? { ...s, id: s.scheduleId } : null;
  },
  idFieldName: 'scheduleId',
});

// --- Resource ---
// Explicit tenantId arg. Returns GqlResourceConnection { items, total, nextCursor, hasMore }
export interface Resource extends BaseEntity {
  id: BackendId; // maps to resourceId from gateway
  tenantId: string;
  resourceName: string;
  resourceCode?: string;
  resourceType?: string;
  resourceCategory?: string;
  resourceStatus: string;
  isLifeCriticalResource: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useResourceRepository = createRepository<Resource>({
  entityName: 'Resource',
  listQuery: gql`
    query ListResources($tenantId: String!, $limit: Int, $cursor: String) {
      listResources(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
        items {
          resourceId
          tenantId
          resourceName
          resourceCode
          resourceType
          resourceCategory
          resourceStatus
          isLifeCriticalResource
          createdAt
          updatedAt
          compressedId
        }
        total
        hasMore
      }
    }
  `,
  getQuery: gql`
    query GetResource($tenantId: String!, $resourceId: String!) {
      getResource(tenantId: $tenantId, resourceId: $resourceId) {
        resourceId
        tenantId
        resourceName
        resourceCode
        resourceType
        resourceCategory
        resourceStatus
        isLifeCriticalResource
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) =>
    (data?.listResources?.items || []).map((r: any) => ({ ...r, id: r.resourceId })),
  extractEntity: (data: any) => {
    const r = data?.getResource;
    return r ? { ...r, id: r.resourceId } : null;
  },
  idFieldName: 'resourceId',
});

// --- Integration ---
// Explicit tenantId arg. Returns IntegrationConnection { nodes, totalCount, hasNextPage, endCursor }
export interface Integration extends BaseEntity {
  id: BackendId; // maps to integrationId from gateway
  tenantId: string;
  integrationName: string;
  integrationCode?: string;
  description?: string;
  integrationType?: string;
  currentHealthStatus?: string;
  isLifeCritical: boolean;
  createdAt: string;
  updatedAt: string;
  compressedId?: string;
}

export const useIntegrationRepository = createRepository<Integration>({
  entityName: 'Integration',
  listQuery: gql`
    query ListIntegrations($tenantId: String!, $limit: Int, $cursor: String) {
      listIntegrations(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
        nodes {
          integrationId
          tenantId
          integrationName
          integrationCode
          description
          integrationType
          currentHealthStatus
          isLifeCritical
          createdAt
          updatedAt
          compressedId
        }
        totalCount
        hasNextPage
      }
    }
  `,
  getQuery: gql`
    query GetIntegration($tenantId: String!, $integrationId: String!) {
      getIntegration(tenantId: $tenantId, integrationId: $integrationId) {
        integrationId
        tenantId
        integrationName
        integrationCode
        description
        integrationType
        currentHealthStatus
        isLifeCritical
        createdAt
        updatedAt
        compressedId
      }
    }
  `,
  extractList: (data: any) =>
    (data?.listIntegrations?.nodes || []).map((n: any) => ({ ...n, id: n.integrationId })),
  extractEntity: (data: any) => {
    const i = data?.getIntegration;
    return i ? { ...i, id: i.integrationId } : null;
  },
  idFieldName: 'integrationId',
});
